import { useEffect, useState } from "react";

// Safari (pre-16.4) only exposes the webkit-prefixed Fullscreen API —
// mirrors the same check in src/components/TreeView/hooks/useFullscreen.ts.
interface WebkitFullscreenDocument extends Document {
  webkitFullscreenElement?: Element | null;
}

// The element useFullscreen.ts is simulating fullscreen on, when the native
// Fullscreen API is unavailable or fails to engage (notably iOS WebKit). A
// single ambient reference, mirroring document.fullscreenElement itself.
let simulatedFullscreenElement: Element | null = null;
const simulatedFullscreenListeners = new Set<() => void>();

export const setSimulatedFullscreenElement = (element: Element | null): void => {
  simulatedFullscreenElement = element;
  simulatedFullscreenListeners.forEach((listener) => listener());
};

const subscribeToSimulatedFullscreenChange = (listener: () => void): (() => void) => {
  simulatedFullscreenListeners.add(listener);
  return () => simulatedFullscreenListeners.delete(listener);
};

/**
 * Resolves where portal-based UI (modals, panels, backdrops) should mount.
 *
 * The native Fullscreen API only paints the fullscreened element and its
 * descendants — anything portaled to document.body (the default target for
 * most modal libraries, including MUI's Modal/Portal, or a manually-called
 * ReactDOM.createPortal) becomes invisible while a fullscreen element is
 * active, even though it's still "open" in React state. Passing this as a
 * portal's target keeps such UI visible by mounting inside the active
 * fullscreen element instead — or, when useFullscreen.ts has fallen back to
 * simulated (CSS-driven) fullscreen, inside that element instead.
 */
export const getFullscreenPortalTarget = (): Element => {
  const doc = document as WebkitFullscreenDocument;
  return doc.fullscreenElement ?? doc.webkitFullscreenElement ?? simulatedFullscreenElement ?? document.body;
};

/**
 * Live version of getFullscreenPortalTarget() — re-resolves on every
 * fullscreenchange, and on every simulated-fullscreen change, so a portal
 * already open when the user enters/exits fullscreen (e.g. TreeView's
 * fullscreen mode, real or simulated) follows along automatically.
 */
export const useFullscreenPortalTarget = (): Element => {
  const [portalTarget, setPortalTarget] = useState<Element>(getFullscreenPortalTarget);

  useEffect(() => {
    const updatePortalTarget = () => setPortalTarget(getFullscreenPortalTarget());
    document.addEventListener("fullscreenchange", updatePortalTarget);
    document.addEventListener("webkitfullscreenchange", updatePortalTarget);
    const unsubscribe = subscribeToSimulatedFullscreenChange(updatePortalTarget);
    return () => {
      document.removeEventListener("fullscreenchange", updatePortalTarget);
      document.removeEventListener("webkitfullscreenchange", updatePortalTarget);
      unsubscribe();
    };
  }, []);

  return portalTarget;
};
